import type { AccountBundleV1, EncryptedEntityV1, EntityType, KeyEnvelopeV1, PublicKeysV1 } from './types';
import { aesGcmDecrypt, aesGcmEncrypt, hpkeOpen, hpkeSeal, randomBytes, sign, verify } from './primitives';
import { asBytes, base64url, canonicalBytes, fromUtf8, unbase64url, withoutSignature, type Binary } from './encoding';

export function entityAad(value: Pick<EncryptedEntityV1, 'cryptoVersion' | 'entityType' | 'entityID' | 'ownerID' | 'revision' | 'plaintextSchemaVersion'>) {
  return canonicalBytes({
    cryptoVersion: value.cryptoVersion, entityType: value.entityType, entityID: value.entityID,
    ownerID: value.ownerID, revision: value.revision, plaintextSchemaVersion: value.plaintextSchemaVersion,
  });
}

export function envelopeAad(value: Omit<KeyEnvelopeV1, 'signature' | 'ciphertext'>) {
  return canonicalBytes({
    cryptoVersion: value.cryptoVersion, keyEnvelopeID: value.keyEnvelopeID, entityID: value.entityID,
    recipientID: value.recipientID, recipientType: value.recipientType, groupEpoch: value.groupEpoch ?? null,
    algorithm: value.algorithm, senderID: value.senderID, senderKeyRevision: value.senderKeyRevision,
  });
}

export async function encryptEntity(
  bundle: AccountBundleV1,
  input: { entityType: EntityType; entityID: string; ownerID: string; revision: number; createdAt: string; plaintext: unknown; entityKey?: Binary },
): Promise<{ entity: EncryptedEntityV1; entityKey: Uint8Array }> {
  if (!Number.isSafeInteger(input.revision) || input.revision < 1) throw new Error('Ungültige Entitätsrevision.');
  const unsigned = {
    cryptoVersion: 1 as const, entityType: input.entityType, entityID: input.entityID, ownerID: input.ownerID,
    revision: input.revision, plaintextSchemaVersion: 1 as const, createdAt: input.createdAt,
  };
  const entityKey = input.entityKey ? asBytes(input.entityKey) : randomBytes(32);
  if (entityKey.length !== 32) throw new Error('Entitätsschlüssel muss 32 Byte lang sein.');
  const sealed = await aesGcmEncrypt(entityKey, canonicalBytes(input.plaintext), entityAad(unsigned));
  const signable = {
    ...unsigned, nonce: base64url(sealed.nonce), ciphertext: base64url(sealed.ciphertext),
    signerUserID: input.ownerID, signerKeyRevision: bundle.keyRevision,
  };
  return { entity: { ...signable, signature: await sign(bundle, signable) }, entityKey };
}

export async function decryptEntity<T>(entity: EncryptedEntityV1, entityKey: Binary, signer: PublicKeysV1): Promise<T> {
  if (entity.signerUserID !== signer.userID || entity.signerKeyRevision !== signer.keyRevision) throw new Error('Unpassende Signaturschlüsselrevision.');
  if (!await verify(signer.signingPublicKey, withoutSignature(entity as unknown as Record<string, unknown>), entity.signature)) throw new Error('Entitätssignatur ist ungültig.');
  const plaintext = await aesGcmDecrypt(entityKey, unbase64url(entity.nonce, 12), unbase64url(entity.ciphertext), entityAad(entity));
  return JSON.parse(fromUtf8(plaintext)) as T;
}

export async function createOwnerEnvelope(
  bundle: AccountBundleV1, entityID: string, owner: PublicKeysV1, entityKey: Binary, envelopeID = crypto.randomUUID(),
): Promise<KeyEnvelopeV1> {
  const header = {
    cryptoVersion: 1 as const, keyEnvelopeID: envelopeID, entityID, recipientID: owner.userID,
    recipientType: 'owner' as const, groupEpoch: null, algorithm: 'hpke-p256-sha256-aes256gcm' as const,
    encapsulatedKey: null as string | null, nonce: null as string | null,
    senderID: owner.userID, senderKeyRevision: bundle.keyRevision,
  };
  const info = `holdthat:key-envelope:v1:${entityID}:${owner.userID}`;
  const preliminaryAad = envelopeAad(header);
  const sealed = await hpkeSeal(owner.encryptionPublicKey, entityKey, info, preliminaryAad);
  const signable = { ...header, encapsulatedKey: sealed.encapsulatedKey, ciphertext: sealed.ciphertext };
  return { ...signable, signature: await sign(bundle, signable) };
}

export async function openOwnerEnvelope(bundle: AccountBundleV1, envelope: KeyEnvelopeV1, sender: PublicKeysV1): Promise<Uint8Array> {
  if (envelope.recipientType !== 'owner' || !envelope.encapsulatedKey || envelope.nonce) throw new Error('Kein gültiger Owner-Key-Envelope.');
  if (!await verify(sender.signingPublicKey, withoutSignature(envelope as unknown as Record<string, unknown>), envelope.signature)) throw new Error('Envelope-Signatur ist ungültig.');
  const header = { ...withoutSignature(envelope as unknown as Record<string, unknown>) };
  delete header.ciphertext;
  const info = `holdthat:key-envelope:v1:${envelope.entityID}:${envelope.recipientID}`;
  const key = await hpkeOpen(bundle, envelope.encapsulatedKey, envelope.ciphertext, info, envelopeAad(header as unknown as Omit<KeyEnvelopeV1, 'signature' | 'ciphertext'>));
  if (key.length !== 32) throw new Error('Entitätsschlüssel muss 32 Byte lang sein.');
  return key;
}

export async function createGroupEnvelope(
  bundle: AccountBundleV1, senderID: string, entityID: string, groupID: string, epoch: number, groupKey: Binary, entityKey: Binary, envelopeID = crypto.randomUUID(),
): Promise<KeyEnvelopeV1> {
  const signableHeader = {
    cryptoVersion: 1 as const, keyEnvelopeID: envelopeID, entityID, recipientID: groupID,
    recipientType: 'group' as const, groupEpoch: epoch, algorithm: 'aes256gcm' as const,
    encapsulatedKey: null, nonce: null as string | null, senderID, senderKeyRevision: bundle.keyRevision,
  };
  const nonce = randomBytes(12);
  const header = { ...signableHeader, nonce: base64url(nonce) };
  const sealed = await aesGcmEncrypt(groupKey, entityKey, envelopeAad(header), nonce);
  const signable = { ...header, ciphertext: base64url(sealed.ciphertext) };
  return { ...signable, signature: await sign(bundle, signable) };
}

export async function openGroupEnvelope(envelope: KeyEnvelopeV1, groupKey: Binary, sender: PublicKeysV1): Promise<Uint8Array<ArrayBuffer>> {
  if (envelope.recipientType !== 'group' || !envelope.nonce || envelope.encapsulatedKey) throw new Error('Kein gültiger Gruppen-Key-Envelope.');
  if (!await verify(sender.signingPublicKey, withoutSignature(envelope as unknown as Record<string, unknown>), envelope.signature)) throw new Error('Envelope-Signatur ist ungültig.');
  const header = { ...withoutSignature(envelope as unknown as Record<string, unknown>) }; delete header.ciphertext;
  const key = await aesGcmDecrypt(groupKey, unbase64url(envelope.nonce, 12), unbase64url(envelope.ciphertext), envelopeAad(header as unknown as Omit<KeyEnvelopeV1, 'signature' | 'ciphertext'>));
  if (key.length !== 32) throw new Error('Entitätsschlüssel muss 32 Byte lang sein.');
  return key;
}
